import React, { useCallback, useEffect, useMemo, useState } from "react";

import { publicApi, BACKEND_URL } from "@/api/api";
import {
  blog,
  brand,
  dummyImages,
  hero,
  nav,
  portfolio,
  processSteps,
  seo,
  servicesSection,
} from "@/mock";

function isObject(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

// Recursively prepend BACKEND_URL to any /api/media/ paths
function resolveMediaUrls(value) {
  if (typeof value === "string") {
    return value.startsWith("/api/media/") ? `${BACKEND_URL}${value}` : value;
  }
  if (Array.isArray(value)) return value.map(resolveMediaUrls);
  if (isObject(value)) {
    const out = {};
    for (const k of Object.keys(value)) out[k] = resolveMediaUrls(value[k]);
    return out;
  }
  return value;
}

// Database-first merge: use remote data as primary, fallback to defaults only for missing fields
function mergeWithFallback(remote, fallback) {
  if (remote === undefined || remote === null) return fallback;
  if (Array.isArray(remote)) return remote; // Arrays from DB replace entirely
  if (isObject(remote) && isObject(fallback)) {
    const out = { ...remote };
    // Only add fallback keys that don't exist in remote
    for (const k of Object.keys(fallback)) {
      if (!(k in out) || out[k] === undefined || out[k] === null) {
        out[k] = fallback[k];
      } else if (isObject(out[k]) && isObject(fallback[k])) {
        out[k] = mergeWithFallback(out[k], fallback[k]);
      }
    }
    return out;
  }
  return remote;
}

function applySeo(seoCfg) {
  if (!seoCfg) return;

  if (typeof seoCfg.title === "string") document.title = seoCfg.title;

  const ensureMeta = (nameOrProp, content, isProperty = false) => {
    if (!content) return;
    const selector = isProperty
      ? `meta[property='${nameOrProp}']`
      : `meta[name='${nameOrProp}']`;
    let tag = document.head.querySelector(selector);
    if (!tag) {
      tag = document.createElement("meta");
      if (isProperty) tag.setAttribute("property", nameOrProp);
      else tag.setAttribute("name", nameOrProp);
      document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
  };

  ensureMeta("description", seoCfg.description);
  ensureMeta("keywords", seoCfg.keywords);

  // Open Graph tags
  ensureMeta("og:title", seoCfg.ogTitle || seoCfg.title, true);
  ensureMeta("og:description", seoCfg.ogDescription || seoCfg.description, true);
  
  // OG Image with fallback and dimensions
  const ogImageUrl = seoCfg.ogImage || "/og-image.jpg";
  const fullOgImageUrl = ogImageUrl.startsWith("http") 
    ? ogImageUrl 
    : `https://myhomestaging.ru${ogImageUrl}`;
  ensureMeta("og:image", fullOgImageUrl, true);
  ensureMeta("og:image:width", "1200", true);
  ensureMeta("og:image:height", "630", true);
  
  ensureMeta("og:type", "website", true);
  ensureMeta("og:url", seoCfg.ogUrl || "https://myhomestaging.ru/", true);
  ensureMeta("og:locale", "ru_RU", true);

  // Search engine verification
  ensureMeta("yandex-verification", seoCfg.yandexVerification);
  ensureMeta("google-site-verification", seoCfg.googleVerification);

  // Indexing control - if indexingEnabled is explicitly false, add noindex
  if (seoCfg.indexingEnabled === false) {
    ensureMeta("robots", "noindex, nofollow");
  } else {
    // Remove noindex if it exists and indexing is enabled
    const robotsTag = document.head.querySelector('meta[name="robots"]');
    if (robotsTag && robotsTag.getAttribute("content")?.includes("noindex")) {
      robotsTag.remove();
    }
  }

  if (seoCfg.canonical) {
    let link = document.head.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", seoCfg.canonical);
  }
}

export function useSiteContentApi() {
  const defaults = useMemo(
    () => ({
      blog,
      brand,
      nav,
      hero,
      servicesSection,
      portfolio,
      processSteps,
      dummyImages,
      seo,
    }),
    []
  );

  const [remote, setRemote] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await publicApi.getContent();
      setRemote(resolveMediaUrls(res.data?.content || {}));
    } catch {
      // fallback to defaults
      setRemote(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const content = useMemo(() => {
    // Don't return anything until we have remote data loaded
    // This prevents flash of mock content
    if (loading) return null;
    // Database data is primary, mock data is only fallback for missing fields
    return remote ? mergeWithFallback(remote, defaults) : defaults;
  }, [defaults, remote, loading]);

  useEffect(() => {
    if (content?.seo) {
      applySeo(content.seo);
    }
  }, [content?.seo]);

  return { content, loading, refetch };
}
